import React, { FC, ReactNode, useState } from 'react';
import classes from './CreateGame.module.scss';
import { CSSTransition } from 'react-transition-group';
import { TileBasedGameType } from '../config/tileGameConfig';
import classNames from 'classnames';
import createTileItem from '../envs/ccson/createTileItem';
import { CreateMessageDTO } from '@envclient/envcore/src/types/core';
import GameFamilies from './guide/GameFamilies';
import { GameFamily } from '../config/gameFamilyConfig';
import { FaArrowLeft, FaTimes } from 'react-icons/fa';
import createKTileItem from '../envs/koenigsburg/createKTileItem';
import { ClosableCompProps } from '../components/types';
import Button from '../components/Button';
import Alert from '../components/Alert';
import Modal from '../components/Modal';
import { getUserInfo } from '../service/userService';
import WsConnection from '../service/WsConnection';
import { isSupported } from '../utils/game';
import { TileGameConfig } from '@envclient/envengine/src/envs/tilebase/TileGameConfig';

const CreateGame: FC<ClosableCompProps> = ({ onClose }) => {
  // seamini game is selected by default
  const [selectedType, setSelectedType] = useState<TileGameConfig>();
  const [delay, setDelay] = useState<number>(30);
  const [maxPlayer, setMaxPlayer] = useState<number>(2);
  const [locked, setLocked] = useState<boolean>(true);

  const [selectedFamily, setSelectedFamily] = useState<GameFamily>();
  const [games, setGames] = useState<TileGameConfig[]>();

  const [showCreditAlert, setShowCreditAlert] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);

  async function handleCreateGame() {
    if (!selectedFamily || !selectedType || submitting) return;
    setSubmitting(true);

    // check credits and alert if not enough
    const user = await getUserInfo();
    if (user && user?.credits < selectedType.credits) {
      setShowCreditAlert(true);
      setSubmitting(false);
      return;
    }

    // chess is strictly 2 players; the per-move limit uses the same delay selector as other
    // games. On timeout the env ends the game (the idle player loses) rather than passing the
    // turn, so the board stays in sync with the FEN side-to-move.
    const isChess = selectedFamily?.type === 'chess';
    const gameObj: CreateMessageDTO = {
      type: 'CREATE' as const,
      envType: selectedFamily?.envType,
      gameType: selectedType?.type as TileBasedGameType,
      delay: delay * 1000,
      maxPlayer: isChess ? 2 : maxPlayer,
      locked: locked
    };
    WsConnection.send(gameObj);
    onClose();
  }

  const handleClose = () => {
    onClose();
  };

  const handleSelectFamily = (gf: GameFamily) => {
    setSelectedFamily(gf);
    // chess uses a longer per-move limit by default
    setDelay(gf.type === 'chess' ? 300 : 30);
    const filteredGames = gf.games.filter((gc) => isSupported(gc, window.appConfig.version));
    setGames(filteredGames);
    setSelectedType(filteredGames[0]);
  };

  const handleSelect = (gc: TileGameConfig) => {
    setSelectedType(gc);
  };

  const back = () => {
    setSelectedType(undefined);
    setSelectedFamily(undefined);
  };

  const renderTitle = (): ReactNode => {
    if (selectedFamily && selectedType) {
      return (
        <>
          <Button secondary onClick={back}>
            <FaArrowLeft />
          </Button>
          {selectedFamily.title} / {selectedType.label}
        </>
      );
    } else if (selectedFamily) {
      return (
        <>
          <Button secondary onClick={back}>
            <FaArrowLeft />
          </Button>
          {selectedFamily.title} /
        </>
      );
    }
    return 'Create game:';
  };

  return (
    <>
      {showCreditAlert && (
        <Alert
          title="Credit check"
          message="You dont have enough credits for this game!"
          onClose={() => setShowCreditAlert(false)}
        />
      )}
      <Modal>
        <CSSTransition in={true} timeout={250} classNames="fade">
          <div className={classes.mainCont}>
            <div className={classes.bg}></div>
            <div className={classes.panel}>
              <div className={classes.header}>
                <div className={classes.title}> {renderTitle()}</div>
                <Button secondary onClick={handleClose}>
                  <FaTimes />
                </Button>
              </div>
              {!selectedFamily ? (
                <GameFamilies onSelectFamily={handleSelectFamily} />
              ) : (
                <>
                  <div className={classes.body}>
                    <div className={classes.subtitle}>Game type</div>
                    <div className={classes.gameList}>
                      {games &&
                        games.map((gc: TileGameConfig) => {
                          const gameTypeClasses = classNames(classes.gameType, {
                            [classes.selected]: selectedType === gc
                          });
                          return (
                            <div key={gc.type as string} className={gameTypeClasses} onClick={() => handleSelect(gc)}>
                              <div className={classes.gameTitle}>{gc.label}</div>
                              <div
                                className={classNames(classes.tileCont, {
                                  [classes.koenigsbourg]: gc.family === 'koenigsburg'
                                })}>
                                {gc.family === 'koenigsburg'
                                  ? createKTileItem(gc.thumbnailTile)
                                  : selectedFamily.type === 'chess'
                                  ? <img
                                      src={'./static/public/media/' + selectedFamily.src}
                                      alt="Chess"
                                      style={{ width: '150px', height: '150px' }}
                                    />
                                  : createTileItem(gc.thumbnailTile, selectedFamily.type)}
                              </div>
                              {selectedFamily.type === 'chess' ? (
                                <div className={classes.detail}>
                                  <div className={classes.credits}>{gc.points} points</div>
                                  <div>2 players</div>
                                </div>
                              ) : gc.family === 'koenigsburg' ? (
                                <div className={classes.detail}>
                                  {gc.credits ? <div className={classes.credits}>{gc.credits} credits</div> : null}
                                  <div className={classes.credits}>{gc.points} points</div>
                                  <div>{gc.tiles * 3} tiles</div>
                                  <div>{gc.tiles} turns</div>
                                  <div>{3} castles</div>
                                  <div>{gc.meepleCount.meeple} meeples</div>
                                </div>
                              ) : (
                                <div className={classes.detail}>
                                  {gc.credits ? <div className={classes.credits}>{gc.credits} credits</div> : null}
                                  <div className={classes.credits}>{gc.points} points</div>
                                  <div>{gc.tiles} tiles</div>
                                  <div>{gc.meepleCount.meeple} meeples</div>
                                  {gc.meepleCount.giant && <div>{gc.meepleCount.giant} giants</div>}
                                  {gc.meepleCount.ship && <div>{gc.meepleCount.ship} ships </div>}
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                    <div className={classes.subtitle}>Game options</div>
                    <div className={classes.gameOptions}>
                      <div className={classes.optionRow}>
                        <label htmlFor="delay">{selectedFamily.type === 'chess' ? 'Move time' : 'Game speed'}</label>
                        <select
                          name="delay"
                          id="delay"
                          value={delay}
                          onChange={(e) => setDelay(Number(e.target.value))}>
                          {(selectedFamily.type === 'chess' ? [30, 60, 120, 180, 240, 300] : [15, 30, 45, 60, 90]).map(
                            (s) => (
                              <option key={s} value={s}>
                                {s} sec
                              </option>
                            )
                          )}
                        </select>
                      </div>
                      {selectedFamily.type !== 'chess' && (
                        <div className={classes.optionRow}>
                          <label htmlFor="max">Max players</label>
                          <select
                            name="max"
                            id="max"
                            value={maxPlayer}
                            onChange={(e) => setMaxPlayer(Number(e.target.value))}>
                            <option value={1}>1 player</option>
                            <option value={2}>2 players</option>
                            <option value={3}>3 players</option>
                            <option value={4}>4 players</option>
                          </select>
                        </div>
                      )}
                      <div className={classes.optionRow}>
                        <label htmlFor="locked">Locked</label>
                        <input
                          id="locked"
                          type="checkbox"
                          checked={locked}
                          onChange={(e) => setLocked(Boolean(e.target.checked))}
                        />
                      </div>
                    </div>
                  </div>
                  <div className={classes.btnBar}>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleCreateGame} disabled={submitting} loading={submitting}>
                      Submit
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </CSSTransition>
      </Modal>
    </>
  );
};

export default CreateGame;
